import { useEffect, useState } from "react";
import api, { errMsg, fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { PiggyBank } from "lucide-react";

const Stat = ({ label, value, sub, tone = "" }) => (
  <div className="border p-3"><div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-xl font-black tabular-nums ${tone}`}>{value}</div>{sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}</div>
);
const Pct = ({ v }) => v == null ? <span className="text-muted-foreground">—</span> : <span className={v >= 0 ? "text-emerald-700" : "text-red-700"}>{v >= 0 ? "+" : ""}{v}%</span>;

export function SavingsReportDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const load = () => api.get("/reports/quote-savings", { params: { ...(from && { date_from: from }), ...(to && { date_to: to }) } })
    .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, from, to]);
  const tt = data?.totals;
  const exportCsv = () => {
    const head = ["REQ", "Fecha", "Depto", "OC", "Proveedor ganador", "Cotizaciones", "Costo estimado", "Promedio cotizado", "Ganadora", "Ahorro vs estimado", "Ahorro vs promedio"];
    const lines = data.rows.map((r) => [r.number, r.date, r.department, r.po_number, r.winner_supplier, r.quotes, r.est_total, r.avg_total, r.winner_total, r.savings_vs_est, r.savings_vs_avg]);
    const csv = [head, ...lines].map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" })); a.download = "ahorros_cotizaciones.csv"; a.click();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="savings-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black flex items-center gap-2"><PiggyBank size={16} /> {t("sv.title")}</DialogTitle>
          <DialogDescription className="text-xs">{t("sv.hint")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.filterFrom")}</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none h-8 text-xs" data-testid="savings-from" /></div>
          <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.filterTo")}</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none h-8 text-xs" data-testid="savings-to" /></div>
          {data?.rows?.length > 0 && <Button size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={exportCsv} data-testid="savings-export">CSV</Button>}
        </div>
        {tt && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="savings-totals">
            <Stat label={t("sv.reqs")} value={tt.count} />
            <Stat label={t("sv.vsEst")} value={fmtMoney(tt.savings_vs_est)} sub={<Pct v={tt.pct_vs_est} />} tone={tt.savings_vs_est >= 0 ? "text-emerald-700" : "text-red-700"} />
            <Stat label={t("sv.vsAvg")} value={fmtMoney(tt.savings_vs_avg)} sub={<Pct v={tt.pct_vs_avg} />} tone={tt.savings_vs_avg >= 0 ? "text-emerald-700" : "text-red-700"} />
            <Stat label={t("sv.vsHighest")} value={fmtMoney(tt.savings_vs_highest)} tone="text-emerald-700" />
          </div>
        )}
        {data && (data.rows.length === 0 ? <div className="text-xs text-muted-foreground italic">{t("common.noData")}</div> : (
          <div className="tbl-wrap"><table className="tbl" data-testid="savings-table">
            <thead><tr><th>REQ</th><th>{t("common.date")}</th><th>{t("rq.department")}</th><th>OC</th><th>{t("qt.winner")}</th><th className="num">#</th>
              <th className="num">{t("sv.est")}</th><th className="num">{t("sv.avg")}</th><th className="num">{t("sv.winnerTotal")}</th><th className="num">{t("sv.vsEst")}</th><th className="num">{t("sv.vsAvg")}</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.requisition_id}>
                <td className="font-bold">{r.number}</td><td className="tabular-nums">{r.date}</td><td>{r.department || "—"}</td><td className="text-primary font-bold">{r.po_number || "—"}</td>
                <td>{r.winner_supplier}</td><td className="num">{r.quotes}</td>
                <td className="num text-muted-foreground">{fmtMoney(r.est_total)}</td><td className="num text-muted-foreground">{fmtMoney(r.avg_total)}</td>
                <td className="num font-bold">{fmtMoney(r.winner_total)} <span className="text-[9px]">{r.currency}</span></td>
                <td className="num"><b className={r.savings_vs_est >= 0 ? "text-emerald-700" : "text-red-700"}>{fmtMoney(r.savings_vs_est)}</b> <Pct v={r.pct_vs_est} /></td>
                <td className="num"><b className={r.savings_vs_avg >= 0 ? "text-emerald-700" : "text-red-700"}>{fmtMoney(r.savings_vs_avg)}</b> <Pct v={r.pct_vs_avg} /></td>
              </tr>))}</tbody>
          </table></div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
